import React from 'react'
import PropTypes from 'prop-types'
import {BuilderLinkWrapper} from 'hec/builder/blocks/cta/linkWrapper'
import {Button} from 'hec/components/cta'
import {buttonFunctions, coreButtonInputs} from 'hec/builder/blocks/cta/cta.helper'
import {isClient} from '@/utils/common'

export const IconLinksGrid = ({
    items = [],
    includeLiveChatTrigger,
    liveChatTriggerConfig,
    liveChatTriggerItemNumber
}) => {
    const getBorderStyles = (index, totalRows) => {
        const currentRow = Math.floor(index / 2) + 1
        const isLastRow = currentRow === totalRows
        const hasRightBorder = index % 2 === 0
        return `p-5 text-center ${hasRightBorder ? 'border-r-2' : ''} ${
            !isLastRow ? 'border-b-2' : ''
        } border-gray-02`
    }

    const handleChatTriggerClick = () => {
        if (isClient) {
            window.zE.activate()
        }
    }

    const getAllItems = () => {
        if (!includeLiveChatTrigger || !liveChatTriggerConfig) {
            return items
        }
        const itemsWithTrigger = [...items]
        const index = liveChatTriggerItemNumber - 1

        if (index >= itemsWithTrigger.length || !liveChatTriggerItemNumber) {
            itemsWithTrigger.push({id: 'chat-trigger', ...liveChatTriggerConfig})
        } else {
            itemsWithTrigger.splice(index, 0, {id: 'chat-trigger', ...liveChatTriggerConfig})
        }
        return itemsWithTrigger
    }
    const generateLinkItems = () => {
        const allItems = getAllItems()
        const totalRows = Math.ceil(allItems.length / 2)

        return allItems?.map((item, index) => {
            const {label, icon, isRevShare, ...buttonProps} = item

            if (item.id === 'chat-trigger') {
                return (
                    <Button
                        onClick={handleChatTriggerClick}
                        clickFunction={buttonFunctions.nothing}
                        isRevShare={isRevShare}
                        className={getBorderStyles(index, totalRows)}
                        key={`icon-link-grid-${index}`}
                        data-testid="iconLinkGrid-LinkWrapper"
                    >
                        <div>
                            <div className="mb-3">
                                <img src={icon} alt={label} className="mx-auto w-10 h-10" />
                            </div>
                            <p className="text-lg font-medium">{item.label}</p>
                        </div>
                    </Button>
                )
            }

            return (
                <BuilderLinkWrapper
                    {...buttonProps}
                    isRevShare={isRevShare}
                    attributes={{className: getBorderStyles(index, totalRows)}}
                    key={`icon-link-grid-${index}`}
                    data-testid="iconLinkGrid-LinkWrapper"
                >
                    <div>
                        <div className="mb-3">
                            <img src={icon} alt={label} className="mx-auto w-10 h-10" />
                        </div>
                        <p className="text-lg font-medium">{item.label}</p>
                    </div>
                </BuilderLinkWrapper>
            )
        })
    }

    return (
        <div className="px-2.5 mb-5">
            <div className="grid grid-cols-2">{generateLinkItems()}</div>
        </div>
    )
}

IconLinksGrid.propTypes = {
    items: PropTypes.arrayOf(
        PropTypes.shape({
            icon: PropTypes.string.isRequired,
            label: PropTypes.string.isRequired
        })
    ),
    includeLiveChatTrigger: PropTypes.bool,
    liveChatTriggerConfig: PropTypes.shape({
        icon: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired
    }),
    liveChatTriggerItemNumber: PropTypes.number
}

export const IconLinksGridDefinition = {
    component: IconLinksGrid,
    name: 'IconLinksGrid',
    image: 'https://unpkg.com/@mdi/svg@7.4.47/svg/grid.svg',
    inputs: [
        {
            name: 'items',
            type: 'list',
            subFields: [
                {
                    name: 'icon',
                    type: 'file',
                    allowedFileTypes: ['svg'],
                    required: true,
                    helperText: 'Upload an SVG icon for the item.'
                },
                {
                    name: 'label',
                    type: 'string',
                    required: true,
                    helperText: 'The text label for the item.'
                },
                ...coreButtonInputs
            ],
            helperText: 'The array of items with an uploaded SVG icon, label, and link.'
        },
        {name: 'includeLiveChatTrigger', type: 'boolean', defaultValue: true},
        {
            name: 'liveChatTriggerConfig',
            type: 'object',
            subFields: [
                {
                    name: 'icon',
                    type: 'file',
                    allowedFileTypes: ['svg'],
                    required: true,
                    helperText: 'Upload an SVG icon for the item.'
                },
                {
                    name: 'label',
                    type: 'string',
                    required: true,
                    helperText: 'The text label for the item.'
                }
            ],
            showIf: `options.get('includeLiveChatTrigger') === true`
        },
        {
            name: 'liveChatTriggerItemNumber',
            type: 'number',
            showIf: `options.get('includeLiveChatTrigger') === true`,
            helperText:
                'Where in the list of icon links you want the chat trigger to appear. Will default to the last place in items list.'
        }
    ]
}
